import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  IconButton,
  Tooltip,
} from "@mui/material";
import { AlertCircle, Check } from "lucide-react";

const OutstandingBillsTable = ({
  bills,
  totalReceiptAmount = 0,
  onAdjustedAmountsChange,
}) => {
  const [adjustedBills, setAdjustedBills] = useState([]);
  const [remainingAmount, setRemainingAmount] = useState(totalReceiptAmount);

  useEffect(() => {
    // Initialize with empty adjusted amounts when bills change
    if (bills) {
      const initialAdjustedBills = bills.map(bill => ({
        ...bill,
        adjustedAmount: 0,
        isSelected: false,
      }));
      setAdjustedBills(initialAdjustedBills);
    }
  }, [bills]);

  useEffect(() => {
    // Update remaining amount
    const totalAdjusted = adjustedBills.reduce(
      (sum, bill) => sum + (parseFloat(bill.adjustedAmount) || 0),
      0,
    );
    setRemainingAmount(totalReceiptAmount - totalAdjusted);

    // Notify parent component about adjusted amounts
    if (onAdjustedAmountsChange) {
      const billsWithAdjustments = adjustedBills.filter(
        bill => (parseFloat(bill.adjustedAmount) || 0) > 0,
      );
      onAdjustedAmountsChange(billsWithAdjustments);
    }
  }, [adjustedBills, totalReceiptAmount, onAdjustedAmountsChange]);

  const handleAdjustmentChange = (index, value) => {
    const numericValue = parseFloat(value) || 0;
    const bill = adjustedBills[index];

    // Ensure adjustment doesn't exceed pending amount
    const adjustedValue = Math.min(numericValue, bill.pendingAmount);

    const newAdjustedBills = [...adjustedBills];
    newAdjustedBills[index] = {
      ...bill,
      adjustedAmount: adjustedValue,
      isSelected: adjustedValue > 0,
    };

    setAdjustedBills(newAdjustedBills);
  };

  const handleSelectBill = (index, checked) => {
    const bill = adjustedBills[index];
    const newAdjustedBills = [...adjustedBills];

    if (checked) {
      // If selecting, adjust the amount to either pending amount or remaining receipt amount, whichever is smaller
      const adjustedValue = Math.min(bill.pendingAmount, remainingAmount);

      newAdjustedBills[index] = {
        ...bill,
        adjustedAmount: adjustedValue,
        isSelected: true,
      };
    } else {
      // If deselecting, set adjustment to 0
      newAdjustedBills[index] = {
        ...bill,
        adjustedAmount: 0,
        isSelected: false,
      };
    }

    setAdjustedBills(newAdjustedBills);
  };

  const adjustFullAmount = () => {
    let amountToDistribute = totalReceiptAmount;
    const newAdjustedBills = [...adjustedBills].map(bill => ({
      ...bill,
      adjustedAmount: 0,
      isSelected: false,
    }));

    // Sort bills by date (oldest first)
    const sortedBillIndices = newAdjustedBills
      .map((bill, index) => ({
        index,
        date: new Date(bill.billDate.split("/").reverse().join("-")),
      }))
      .sort((a, b) => a.date - b.date)
      .map(item => item.index);

    // Distribute amount across bills
    for (const index of sortedBillIndices) {
      if (amountToDistribute <= 0) break;

      const bill = newAdjustedBills[index];
      const adjustedValue = Math.min(bill.pendingAmount, amountToDistribute);

      if (adjustedValue > 0) {
        newAdjustedBills[index] = {
          ...bill,
          adjustedAmount: adjustedValue,
          isSelected: true,
        };

        amountToDistribute -= adjustedValue;
      }
    }

    setAdjustedBills(newAdjustedBills);
  };

  if (!bills || bills.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <div className="flex justify-center mb-4">
          <AlertCircle size={24} className="text-gray-400" />
        </div>
        <p className="text-gray-500 font-outfit">No outstanding bills found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 font-outfit">
            Remaining Amount:
          </span>
          <span
            className={`font-semibold ${
              remainingAmount < 0 ? "text-red-600" : "text-[#5652B0]"
            }`}>
            ₹{remainingAmount.toLocaleString()}
          </span>
          {remainingAmount < 0 && (
            <Tooltip title="Adjusted amount exceeds receipt amount">
              <AlertCircle size={16} className="text-red-600" />
            </Tooltip>
          )}
        </div>

        <Tooltip title="Auto-adjust bills (oldest first)">
          <button
            onClick={adjustFullAmount}
            className="flex items-center bg-[#F9F8FE] text-[#5652B0] px-3 py-1.5 rounded-md text-sm font-medium hover:bg-[#EFEDFB] transition-colors">
            <Check size={16} className="mr-1.5" />
            Auto Adjust
          </button>
        </Tooltip>
      </div>

      <TableContainer
        component={Paper}
        className="shadow-sm border border-gray-200 rounded-lg">
        <Table aria-label="outstanding bills table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
              <TableCell padding="checkbox" align="center">
                <span className="text-gray-500 text-xs font-medium">
                  Select
                </span>
              </TableCell>
              <TableCell>
                <span className="text-gray-500 text-xs font-medium">
                  Bill Number
                </span>
              </TableCell>
              <TableCell>
                <span className="text-gray-500 text-xs font-medium">
                  Bill Date
                </span>
              </TableCell>
              <TableCell align="right">
                <span className="text-gray-500 text-xs font-medium">
                  Total Amount
                </span>
              </TableCell>
              <TableCell align="right">
                <span className="text-gray-500 text-xs font-medium">
                  Pending
                </span>
              </TableCell>
              <TableCell align="right">
                <span className="text-gray-500 text-xs font-medium">
                  Adjust Amount
                </span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adjustedBills.map((bill, index) => (
              <TableRow
                key={bill.id}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  backgroundColor: bill.isSelected
                    ? "rgba(86, 82, 176, 0.04)"
                    : "inherit",
                }}>
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    checked={bill.isSelected}
                    onChange={e => handleSelectBill(index, e.target.checked)}
                    sx={{
                      color: "#5652B0",
                      "&.Mui-checked": {
                        color: "#5652B0",
                      },
                    }}
                  />
                </TableCell>
                <TableCell component="th" scope="row" className="font-outfit">
                  {bill.billNumber}
                </TableCell>
                <TableCell className="font-outfit">{bill.billDate}</TableCell>
                <TableCell align="right" className="font-outfit">
                  ₹{bill.totalAmount.toLocaleString()}
                </TableCell>
                <TableCell align="right" className="font-outfit">
                  ₹{bill.pendingAmount.toLocaleString()}
                </TableCell>
                <TableCell align="right" width="180px">
                  <TextField
                    size="small"
                    type="number"
                    value={bill.adjustedAmount}
                    onChange={e =>
                      handleAdjustmentChange(index, e.target.value)
                    }
                    InputProps={{
                      startAdornment: (
                        <span className="text-gray-400 mr-1">₹</span>
                      ),
                      inputProps: {
                        min: 0,
                        max: bill.pendingAmount,
                        style: { textAlign: "right" },
                      },
                    }}
                    disabled={!bill.isSelected}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: bill.isSelected ? "#5652B0" : "#E5E7EB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#5652B0",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#5652B0",
                        },
                      },
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default OutstandingBillsTable;
